/**
 * Product Specifications Accordion
 * File: assets/product-specifications.js
 *
 * - Smooth max-height animation (no layout shift)
 * - Full keyboard & screen-reader accessible
 * - Respects prefers-reduced-motion
 * - Supports multiple sections on one page
 * - Reads "open_by_default" from data attribute
 */

(function () {
  'use strict';

  const OPEN_CLASS = 'is-open';

  /**
   * Measure the natural scroll height of the panel content
   * by temporarily making it visible off-screen.
   */
  function getPanelHeight(panel) {
    // Temporarily allow full height to measure
    panel.style.maxHeight = 'none';
    panel.style.visibility = 'hidden';
    panel.removeAttribute('hidden');
    const height = panel.scrollHeight;
    // Reset
    panel.style.maxHeight = '';
    panel.style.visibility = '';
    panel.setAttribute('hidden', '');
    return height;
  }

  function openPanel(trigger, panel) {
    const height = getPanelHeight(panel);

    // Set CSS variable so transition targets the real height
    panel.style.setProperty('--specs-panel-height', height + 'px');

    // Remove hidden attr (we override display via CSS to keep it block)
    panel.removeAttribute('hidden');

    // Use rAF to ensure the browser registers the height before transitioning
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        panel.classList.add(OPEN_CLASS);
        trigger.setAttribute('aria-expanded', 'true');
      });
    });
  }

  function closePanel(trigger, panel) {
    // Capture current rendered height before collapsing
    const currentHeight = panel.scrollHeight;
    panel.style.setProperty('--specs-panel-height', currentHeight + 'px');

    // Force repaint at current height, then animate to 0
    requestAnimationFrame(function () {
      panel.classList.remove(OPEN_CLASS);
      trigger.setAttribute('aria-expanded', 'false');

      // Re-add hidden after transition ends for accessibility
      const duration = parseFloat(
        getComputedStyle(panel).transitionDuration || '0.38'
      ) * 1000;

      setTimeout(function () {
        if (!panel.classList.contains(OPEN_CLASS)) {
          panel.setAttribute('hidden', '');
        }
      }, duration);
    });
  }

  function initAccordion(section) {
    const trigger = section.querySelector('.prod-specs__trigger');
    const panel = section.querySelector('.prod-specs__panel');

    if (!trigger || !panel) return;

    // Check "open by default" — stored as data attribute on the section
    const openByDefault = section.dataset.openByDefault === 'true';

    if (openByDefault) {
      openPanel(trigger, panel);
    }

    trigger.addEventListener('click', function () {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        closePanel(trigger, panel);
      } else {
        openPanel(trigger, panel);
      }
    });

    // Keyboard: allow Enter/Space to toggle (button already handles this,
    // but explicit for custom key-intercept scenarios)
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        trigger.click();
      }
    });
  }

  function init() {
    const sections = document.querySelectorAll('[data-section-id]');
    sections.forEach(function (section) {
      if (section.classList.contains('product-specifications-section') ||
          section.querySelector('.prod-specs__trigger')) {
        initAccordion(section);
      }
    });
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor re-init support
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target;
    if (section && section.querySelector('.prod-specs__trigger')) {
      initAccordion(section);
    }
  });


  const pdpLandingHeaderSpecs = document.querySelector('.pdp-landing__nav-links a[href="#specs"]');

  if (pdpLandingHeaderSpecs) {
    pdpLandingHeaderSpecs.addEventListener('click', function (e) {
      e.preventDefault();

      const section = document.querySelector('#specs');
      if (!section) return;

      const trigger = section.querySelector('.prod-specs__trigger');
      const panel = section.querySelector('.prod-specs__panel');

      // Scroll first
      section.scrollIntoView({ behavior: 'smooth' });

      // Open accordion if closed
      if (trigger && panel && trigger.getAttribute('aria-expanded') !== 'true') {
        openPanel(trigger, panel);
      }
    });
  }

})();
